import gql from 'graphql-tag';
import moment from 'moment';
import React, {useEffect} from 'react';

import {parseUri} from 'BenchlingUtil';
import {urlToBase64} from 'external-files/ExternalFileUtils';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as browser from 'lib/Browser';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import getOpenVersionForClass from 'notebook/notes/util/getOpenVersionForClass';
import notifications from 'notifications';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import lazyInit from 'util/lazyInit';

import {SignedFileQuery, SignedFileQueryVariables} from './__generated/SignedFileQuery';

interface BaseSignedExtFileURLProxyProps {
  url: string;
  owningEntityApiId: string;
  versionId?: number | null;
  renderWithSignedURL: (
    /**
     * - `undefined` if not loaded
     * - `null` if an error occurred while loading,
     * - and otherwise, a `URL` (string) corresponding to a signed version of @props.url */
    url: string | null | undefined
  ) => JSX.Element;
  /**
   * If true and the S3 file's inferred mimetype is text/html, the URL will be signed with a
   * Content-Type: text/html header so that the file can be rendered in an iframe.
   *
   * @dangerous do not use without approval from Security.
   * @default false
   */
  addHtmlContentTypeHeader?: boolean;
  /**
   * @default false
   */
  useRedirectURL?: boolean;
  /**
   * @default false
   */
  returnBase64?: boolean;
}

interface MaybeRenderWithSignedURLProps extends BaseSignedExtFileURLProxyProps {
  externalFileId?: string | null;
}

interface SignedExtFileURLProxyProps extends BaseSignedExtFileURLProxyProps {
  externalFileId: string;
}

export const SIGNED_FILE_QUERY = gql`
  query SignedFileQuery(
    $apiId: StrictString!
    $url: StrictString!
    $addHtmlContentTypeHeader: StrictBoolean!
    $owningEntityApiId: StrictString
    $versionId: StrictInt
  ) @skipAudit @engComponent(name: NOTEBOOK) {
    signedFile(
      apiId: $apiId
      url: $url
      addHtmlContentTypeHeader: $addHtmlContentTypeHeader
      owningEntityApiId: $owningEntityApiId
      versionId: $versionId
    ) {
      ... on SignedFileNode {
        signedUrl
        redirectUrl
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Determine if a signed S3 URL is expired by parsing its query arguments.
 *
 * Returns true if the correct query arguments are not present.
 *
 * @example
 * // X-Amz-Date is 29/08/2022 6:19:29PM, and X-Amz-Expires is 30 (seconds); so this function would return
 * // true after 6:19:59PM, and false before.
 * isS3URLExpired("https://...s3.amazonaws.com/...?X-Amz-Date=20220829T181929Z&X-Amz-Expires=30")
 */
function isS3URLExpired(signedURL: string): boolean {
  const expiration = parseUri(signedURL).queryKey['X-Amz-Expires'];
  const signatureDateString = parseUri(signedURL).queryKey['X-Amz-Date'];
  if (expiration == null || signatureDateString == null) {
    // If the URL does not contain the required URL parameters to properly determine expiration, act as if it
    // is expired so that the SIGNED_FILE_QUERY is always retriggered.
    return true;
  }

  const signatureDate = moment(signatureDateString);
  return (
    moment(signatureDate)
      .add(expiration, 's')
      // Subtract 3 seconds to account for the time to initiate the S3 network request (which for components
      // like the Google Docs previewer could be a few seconds).
      .subtract(3, 's')
      // Determine if the expiration date has already happened.
      // From the moment docs: "If nothing is passed to moment#isBefore, it will default to the current time.""
      .isBefore()
  );
}

const getCachedSignedURLState = lazyInit(() =>
  makeUseGlobalClientState<string | undefined | null>(undefined)
);
const getCachedImageBase64State = lazyInit(() =>
  makeUseGlobalClientState<string | undefined | null>(undefined)
);

/*
  In some cases, we need to render a file whose contents are not persisted in the database. In these cases,
  the url provided must already be a signed url to the file.
*/
export function MaybeRenderWithSignedURL(props: MaybeRenderWithSignedURLProps): JSX.Element {
  // AWS documentation suggests that signed urls are identifiable by the presence of X-Amz-Expires
  if (props.externalFileId == null && props.url.includes('X-Amz-Expires')) {
    return props.renderWithSignedURL(props.url);
  }
  // If the url is not a signed url, an externalFileId must be provided to render the file.
  if (props.externalFileId == null) {
    notifications.error(
      'An unexpected error has occurred. Please contact Benchling support if the issue persists.'
    );
    return <></>;
  }
  return <SignedExtFileURLProxy {...props} externalFileId={props.externalFileId} />;
}

export default function SignedExtFileURLProxy({
  renderWithSignedURL,
  url,
  externalFileId,
  addHtmlContentTypeHeader = false,
  useRedirectURL = false,
  returnBase64 = false,
  owningEntityApiId,
  versionId = null,
}: SignedExtFileURLProxyProps): JSX.Element {
  const cachedSignedURLState = getCachedSignedURLState();
  const cachedImageBase64State = getCachedImageBase64State();
  const [signedURL, setSignedURL] = cachedSignedURLState.useGlobalClientState(externalFileId);
  const [imageBase64, setImageBase64] = cachedImageBase64State.useGlobalClientState(externalFileId);
  const {isExportView} = EditorPropertiesContext.useContext();
  if (!versionId) {
    versionId = getOpenVersionForClass(owningEntityApiId);
  }
  // Request is done in GraphQL to take advantage of Apollo/GraphQL's autobatching.
  const result = useBenchlingQuery<SignedFileQuery, SignedFileQueryVariables>(SIGNED_FILE_QUERY, {
    variables: {
      apiId: externalFileId,
      url,
      addHtmlContentTypeHeader,
      owningEntityApiId,
      versionId,
    },
    // Note: It's important to use network-only instead of no-cache, so that the query result gets written to
    // the Apollo cache and can be reused the next time this component mounts.
    fetchPolicy:
      signedURL == null || useRedirectURL || isS3URLExpired(signedURL) ? 'network-only' : 'cache-first',
    onCompleted: (data) => {
      if (!isInaccessible(data.signedFile)) {
        setSignedURL(data.signedFile.signedUrl);
      }
    },
  });

  useEffect(() => {
    async function setBase64(): Promise<void> {
      if (signedURL && !isExportView && returnBase64 && !imageBase64) {
        const base64Data = await urlToBase64(signedURL)?.catch((_) => {
          setImageBase64(null);
        });
        if (base64Data) {
          setImageBase64(base64Data);
        }
      }
    }
    setBase64();
  }, [signedURL, returnBase64, setImageBase64, imageBase64, isExportView]);

  if (!returnBase64 || !browser.isBrowser || isExportView) {
    if (result.type === QueryResultType.PENDING) {
      return renderWithSignedURL(undefined);
    } else if (result.type === QueryResultType.ERROR || isInaccessible(result.data.signedFile)) {
      return renderWithSignedURL(null);
    }

    if (useRedirectURL) {
      return renderWithSignedURL(result.data.signedFile.redirectUrl);
    } else {
      return renderWithSignedURL(result.data.signedFile.signedUrl);
    }
  } else if (imageBase64) {
    return renderWithSignedURL(imageBase64);
  } else if (imageBase64 === undefined) {
    return renderWithSignedURL(undefined);
  } else {
    return renderWithSignedURL(null);
  }
}
